import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Divider,
  Stack,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  PictureAsPdf,
  Description,
  OpenInNew,
  Close,
  ExpandMore,
  QuestionAnswer,
} from "@mui/icons-material";
import axios from "axios";

const SUPPORTED_LANGUAGES = [
  { code: "en", label: "English" },
  { code: "hi", label: "Hindi" },
  { code: "mr", label: "Marathi" },
  { code: "gu", label: "Gujarati" },
  { code: "bn", label: "Bengali" },
  { code: "ta", label: "Tamil" },
  { code: "te", label: "Telugu" },
  { code: "kn", label: "Kannada" },
  { code: "ml", label: "Malayalam" },
  { code: "pa", label: "Punjabi" },
  { code: "ur", label: "Urdu" },
  { code: "ar", label: "Arabic" },
  { code: "zh-CN", label: "Chinese (Simplified)" },
  { code: "zh-TW", label: "Chinese (Traditional)" },
  { code: "fr", label: "French" },
  { code: "de", label: "German" },
  { code: "es", label: "Spanish" },
  { code: "pt", label: "Portuguese" },
  { code: "ru", label: "Russian" },
  { code: "ja", label: "Japanese" },
  { code: "ko", label: "Korean" },
  { code: "it", label: "Italian" },
  { code: "tr", label: "Turkish" },
  { code: "nl", label: "Dutch" },
  { code: "pl", label: "Polish" },
  { code: "sv", label: "Swedish" },
  { code: "da", label: "Danish" },
  { code: "fi", label: "Finnish" },
  { code: "no", label: "Norwegian" },
  { code: "id", label: "Indonesian" },
  { code: "ms", label: "Malay" },
  { code: "th", label: "Thai" },
  { code: "vi", label: "Vietnamese" },
  { code: "ro", label: "Romanian" },
  { code: "hu", label: "Hungarian" },
  { code: "cs", label: "Czech" },
  { code: "sk", label: "Slovak" },
  { code: "uk", label: "Ukrainian" },
  { code: "el", label: "Greek" },
  { code: "he", label: "Hebrew" },
  { code: "fa", label: "Persian" },
  { code: "sw", label: "Swahili" },
  { code: "af", label: "Afrikaans" },
];

const translateText = async (html, targetLang) => {
  const url = `https://translate.googleapis.com/translate_a/single?client=gtx&sl=auto&tl=${targetLang}&dt=t&q=${encodeURIComponent(html)}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error("Translation request failed");
  const json = await res.json();
  return json[0].map((chunk) => chunk[0]).join("");
};

const parseDescription = (description) => {
  if (!description) return "";
  if (Array.isArray(description)) return description.map((d) => d.insert || "").join("");
  if (typeof description === "string" && description.startsWith('"')) return JSON.parse(description);
  return description;
};

const getFileIcon = (url) => {
  if (/\.pdf$/i.test(url)) return <PictureAsPdf sx={{ color: "#e53935" }} />;
  if (/\.(mp4|webm|ogg|mov|avi)$/i.test(url))
    return <i className="ri-video-line" style={{ fontSize: 20, color: "#7b1fa2" }} />;
  return <Description sx={{ color: "#1565c0" }} />;
};

const FilePreviewDialog = ({ url, onClose }) => (
  <Dialog open={!!url} onClose={onClose} maxWidth="md" fullWidth>
    <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
      <Typography fontWeight={600} fontSize={14} noWrap>
        {url?.split("/").pop()}
      </Typography>
      <IconButton onClick={onClose} size="small">
        <Close />
      </IconButton>
    </DialogTitle>
    <DialogContent sx={{ p: 2, textAlign: "center", minHeight: 300 }}>
      {/\.(jpg|jpeg|png|gif|webp)$/i.test(url) ? (
        <img src={url} alt="preview" style={{ maxWidth: "100%", maxHeight: "70vh", borderRadius: 8, objectFit: "contain" }} />
      ) : /\.pdf$/i.test(url) ? (
        <iframe src={url} title="PDF Preview" width="100%" height="500px" style={{ border: "none", borderRadius: 8 }} />
      ) : /\.(mp4|webm|ogg|mov|avi)$/i.test(url) ? (
        <video controls autoPlay style={{ maxWidth: "100%", maxHeight: "70vh", borderRadius: 8 }}>
          <source src={url} />
        </video>
      ) : (
        <Box sx={{ py: 6 }}>
          <Typography color="text.secondary" mb={2}>Preview not available for this file type.</Typography>
          <Button variant="outlined" onClick={() => window.open(url, "_blank")}>Open in new tab</Button>
        </Box>
      )}
    </DialogContent>
  </Dialog>
);

const FAQDialog = ({ open, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  const [selectedLang, setSelectedLang] = useState("en");
  const [translatedText, setTranslatedText] = useState("");
  const [isTranslating, setIsTranslating] = useState(false);
  const [translateError, setTranslateError] = useState("");

  useEffect(() => {
    if (!open) {
      setData(null);
      setPreviewUrl(null);
      setTranslatedText("");
    setSelectedLang("en");
    setTranslateError("");
      return;
    }

    const fetchFAQ = async () => {
      setLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/v1/dashboard-information/getDashboardInfo`,
          { params: { pageName: "FAQ" } },
        );
        if (response.data?.success) {
          setData(response.data.data || null);
        } else {
          setData(null);
        }
      } catch {
        setData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchFAQ();
  }, [open]);

  const descriptionHtml = parseDescription(data?.description);
  const files = Array.isArray(data?.file) ? data.file : [];
  const fileUrls = files.map((f) => f.path);

  const imageFiles = fileUrls.filter((url) => /\.(jpg|jpeg|png|gif|webp)$/i.test(url));
  const videoFiles = fileUrls.filter((url) => /\.(mp4|webm|ogg|mov|avi)$/i.test(url));
  const otherFiles = fileUrls.filter(
    (url) => !/\.(jpg|jpeg|png|gif|webp)$/i.test(url) && !/\.(mp4|webm|ogg|mov|avi)$/i.test(url),
  );

  const handleLanguageChange = async (langCode) => {
    setSelectedLang(langCode);
    setTranslateError("");
    if (langCode === "en") { setTranslatedText(""); return; }
    if (!descriptionHtml) return;
    setIsTranslating(true);
    try {
      const result = await translateText(descriptionHtml, langCode);
      setTranslatedText(result);
    } catch (err) {
      setTranslateError("Translation failed. Please try again.");
      setSelectedLang("en");
      setTranslatedText("");
    } finally {
      setIsTranslating(false);
    }
  };

  const handleResetTranslation = () => {
    setTranslatedText("");
    setSelectedLang("en");
    setTranslateError("");
  };

  const displayedDescription = translatedText || descriptionHtml;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "16px", maxHeight: "90vh" },
        }}
      >
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #673ab7 0%, #9c27b0 100%)",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            py: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                backgroundColor: "rgba(255,255,255,0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <QuestionAnswer sx={{ fontSize: 22, color: "#fff" }} />
            </Box>
            <Box>
              <Typography fontWeight={700} fontSize={18} color="#fff" fontFamily="Aeonik">
                Frequently Asked Questions
              </Typography>
              <Typography fontSize={12} color="rgba(255,255,255,0.75)">
                Help & Resources
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: "#fff" }}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: { xs: 2, sm: 3 }, overflow: "auto" }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress sx={{ color: "#673ab7" }} />
            </Box>
          ) : !data ? (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 8, color: "#aaa" }}>
              <QuestionAnswer sx={{ fontSize: 48, mb: 2, color: "#e0e0e0" }} />
              <Typography fontWeight={600} fontSize={16} color="#bbb">
                No FAQ information available yet.
              </Typography>
            </Box>
          ) : (
            <>
              {descriptionHtml && (
                <Box sx={{ backgroundColor: "#f9f6ff", border: "1px solid #e8d5ff", borderRadius: "10px", p: 3, mb: 3, mt: 1 }}>

                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mb: 1 }}>
                    <Typography fontWeight={600} fontSize={14} color="#673ab7" sx={{ textTransform: "uppercase", letterSpacing: 1 }}>
                      Description & Questions
                    </Typography>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {isTranslating && <CircularProgress size={16} sx={{ color: "#673ab7" }} />}

                      <FormControl size="small" sx={{ minWidth: 190 }}>
                        <InputLabel sx={{ fontSize: 13 }}>🌐 Translate</InputLabel>
                        <Select
                          value={selectedLang}
                          label="🌐 Translate"
                          onChange={(e) => handleLanguageChange(e.target.value)}
                          disabled={isTranslating}
                          sx={{ fontSize: 13, borderRadius: "8px", "& .MuiSelect-select": { py: "8px" } }}
                          MenuProps={{ PaperProps: { sx: { maxHeight: 260, borderRadius: "10px" } } }}
                        >
                          {SUPPORTED_LANGUAGES.map(({ code, label }) => (
                          <MenuItem key={code} value={code} sx={{ fontSize: 13 }}>{label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      {translatedText && (
                      <Tooltip title="Reset to original">
                        <IconButton
                          size="small"
                          onClick={handleResetTranslation}
                          sx={{ color: "#673ab7", border: "1px solid #e8d5ff", borderRadius: "8px" }}
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      )}
                    </Box>
                  </Box>

                  <Divider sx={{ mb: 2, borderColor: "#e8d5ff" }} />

                  {translatedText && (
                    <Typography fontSize={11} color="#9c27b0" mb={1.5} sx={{ fontStyle: "italic" }}>
                      Translated to: {SUPPORTED_LANGUAGES.find((l) => l.code === selectedLang)?.label}
                    </Typography>
                  )}
                  {translateError && (
                    <Typography fontSize={12} color="error" mb={1.5}>{translateError}</Typography>
                  )}

                  <Box
                    fontSize={15}
                    color="#333"
                    lineHeight={1.8}
                    dangerouslySetInnerHTML={{ __html: displayedDescription }}
                    sx={{
                      "& strong": { fontWeight: 700 },
                      "& em": { fontStyle: "italic" },
                      "& u": { textDecoration: "underline" },
                      "& s": { textDecoration: "line-through" },
                      "& p": { margin: 0 },
                    }}
                  />
                </Box>
              )}

              {imageFiles.length > 0 && (
                <Box mb={3}>
                  <Typography fontWeight={600} fontSize={14} color="#673ab7" mb={1.5} sx={{ textTransform: "uppercase", letterSpacing: 1 }}>
                    Images
                  </Typography>
                  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 2 }}>
                    {imageFiles.map((url, i) => (
                      <Box
                        key={i}
                        sx={{
                          position: "relative",
                          borderRadius: "10px",
                          overflow: "hidden",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                          "&:hover .overlay": { opacity: 1 },
                        }}
                      >
                        <img src={url} alt={`file-${i}`} style={{ width: "100%", height: 140, objectFit: "cover", display: "block" }} />
                        <Box
                          className="overlay"
                          sx={{
                            position: "absolute",
                            inset: 0,
                            backgroundColor: "rgba(103,58,183,0.6)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            opacity: 0,
                            transition: "opacity 0.2s",
                          }}
                        >
                          <Tooltip title="Open">
                            <IconButton onClick={() => setPreviewUrl(url)} sx={{ color: "#fff" }}>
                              <OpenInNew />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {otherFiles.length > 0 && (
                <Box mb={3}>
                  <Typography fontWeight={600} fontSize={14} color="#673ab7" mb={1.5} sx={{ textTransform: "uppercase", letterSpacing: 1 }}>
                    Attachments
                  </Typography>
                  <Stack spacing={1}>
                    {otherFiles.map((url, i) => (
                      <Box
                        key={i}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          p: 1.5,
                          border: "1px solid #e0e0e0",
                          borderRadius: "8px",
                          backgroundColor: "#fafafa",
                          "&:hover": { backgroundColor: "#f3e5f5", borderColor: "#ce93d8" },
                          cursor: "pointer",
                          transition: "all 0.2s",
                        }}
                        onClick={() => setPreviewUrl(url)}
                      >
                        {getFileIcon(url)}
                        <Typography fontSize={14} color="#333" flex={1} noWrap>
                          {url.split("/").pop()}
                        </Typography>
                        <OpenInNew sx={{ fontSize: 16, color: "#aaa" }} />
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}

              {videoFiles.length > 0 && (
                <Box mb={3}>
                  <Typography fontWeight={600} fontSize={14} color="#673ab7" mb={1.5} sx={{ textTransform: "uppercase", letterSpacing: 1 }}>
                    Videos
                  </Typography>
                  <Stack spacing={2}>
                    {videoFiles.map((url, i) => (
                      <Box key={i} sx={{ borderRadius: "10px", overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.12)" }}>
                        <video controls style={{ width: "100%", maxHeight: 320, display: "block", backgroundColor: "#000" }}>
                          <source src={url} />
                          Your browser does not support the video tag.
                        </video>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}

              
            </>
          )}
        </DialogContent>
      </Dialog>

      <FilePreviewDialog url={previewUrl} onClose={() => setPreviewUrl(null)} />
    </>
  );
};

export default FAQDialog;
